import React, { useEffect, useMemo, useState } from 'react';
import { Button, Message, Modal } from 'react-frontier2';
import { Trans, useTranslation } from 'react-i18next';
import API from '../API';
import { useUser } from '../AdminHooks';
import UserAccess from '../UserAccess';
import { SetLoading } from '@arema/components/Classes';

interface STPDownloadModalProps{
	open: boolean,
	onClose: ()=>void,
	payouts?: number[]
	onPayoutsCompleted?: (payouts: number[])=>void,
	refunds?: number[],
	onRefundsCompleted?: (refunds: number[])=>void,
}

var STPDownloadModal = (props: STPDownloadModalProps)=>{
	var { t } = useTranslation();
	var { user, hasAccess } = useUser();
	var [loading, setLoading] = useState<boolean>(false);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);

	useEffect(()=>{
		if(!props.open) return;
		if(!hasAccess(UserAccess.CONTABILIDAD.DOWNLOAD_STP_FILE)) return;
		sendDownload();
	}, [props.open, props.refunds, props.payouts]);

	var has_access = useMemo(()=>{
		if(!hasAccess(UserAccess.CONTABILIDAD.DOWNLOAD_STP_FILE)) return false;
		if(props.payouts && props.payouts.length>0){
			return hasAccess(UserAccess.CONTABILIDAD.SET_PAYOUT_COMPLETE);
		}else if(props.refunds && props.refunds.length>0){
			return hasAccess(UserAccess.ORDERS.REFUND_COMPLETE);
		}
		return true;
	}, [user])

	var sendDownload = ()=>{
		if(!hasAccess(UserAccess.CONTABILIDAD.DOWNLOAD_STP_FILE)) return;
		if(props.payouts && props.payouts.length>0){
			sendPayoutsDownload();
		}else if(props.refunds && props.refunds.length>0){
			sendRefundsDownload();
		}
	}

	var sendPayoutsDownload = ()=>{
		API.downloadPayoutSTP(props.payouts).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
		}).catch(err=>{
			return setModalPrompts([t('common.unexpected_code', { code: 'LCL-STPDWMD-PYT-1' })]);
		})
	}

	var sendRefundsDownload = ()=>{
		API.downloadRefundZip(props.refunds).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
		}).catch(err=>{
			return setModalPrompts([t('common.unexpected_code', { code: 'LCL-STPDWMD-RFN-1' })]);
		})
	}

	var sendComplete = (setLoading: SetLoading)=>{
		if(props.payouts && props.payouts.length>0){
			sendPayoutsCompleted(setLoading);
		}else if(props.refunds && props.refunds.length>0){
			sendRefundsCompleted(setLoading);
		}
	}

	var sendPayoutsCompleted = (setLoading: SetLoading)=>{
		if(!hasAccess(UserAccess.CONTABILIDAD.SET_PAYOUT_COMPLETE)) return;
		var sent_payouts = [...props.payouts];
		setLoading(true);
		API.completePayouts(sent_payouts).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			if(props.onPayoutsCompleted){
				props.onPayoutsCompleted(sent_payouts)
			}
			if(props.onClose) props.onClose();
		}).catch(err=>{
			return setModalPrompts([t('common.unexpected_code', { code: 'LCL-STPDCPL-PYT-1' })]);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var sendRefundsCompleted = (setLoading: SetLoading)=>{
		if(!hasAccess(UserAccess.ORDERS.REFUND_COMPLETE)) return;
		var sent_refunds = [...props.refunds];
		setLoading(true);
		API.completeRefunds(sent_refunds).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			if(props.onRefundsCompleted){
				props.onRefundsCompleted(sent_refunds)
			}
			if(props.onClose) props.onClose();
		}).catch(err=>{
			return setModalPrompts([t('common.unexpected_code', { code: 'LCL-STPDCPL-RFN-1' })]);
		}).finally(()=>{
			setLoading(false);
		})
	}

	return <Modal open={props.open && !!(props.refunds || props.payouts)} onClose={!has_access ? props.onClose : null} size='mini'>
		<Modal.Header text='Descarga STP' />
		<Modal.Content>
			{!has_access ? (
				<Message centered type='error' style={{ marginTop: 0 }} header={t('common.no_access')}>
					{t('stp_modal.no_access')}
				</Message>
			) : <>
				<Message type='warning' centered>
					<div className="header">{t('stp_modal.confirm_download')}</div>
					<Trans i18nKey={'stp_modal.instructions'} />
					<br />
					{loading ? (
						<div className="fr loader"></div>
					) : !modalPrompts || modalPrompts.length <= 0 ? (
						<Button color='black' onClick={sendComplete} disabled={modalPrompts && modalPrompts.length > 0} text={t('stp_modal.confirm_download')} style={{ marginTop: 15, minWidth: 220 }} />
					) : null}
				</Message>
				<Button basic text={t('stp_modal.retry_download')} style={{ margin: 'auto', marginTop: 15, display: 'block', width: 200 }} color='black' onClick={sendDownload} />
			</>}
		</Modal.Content>
		{!has_access && (
			<Modal.Actions>
				<Button basic text={t('common.close')} onClick={props.onClose} />
			</Modal.Actions>
		)}
	</Modal>
}

export default STPDownloadModal;